/**
 * Acties (kortingen) + uitlichten per artikel. Beheerd in de admin, opgeslagen in
 * Firestore `promotions/{slug}`. Server (createOrder) én client gebruiken dezelfde
 * `salePriceCentsFor`-berekening, zodat de van/voor-weergave en de afrekenprijs
 * altijd kloppen. Prijzen zijn integer eurocenten EXCL. btw (zoals de rest).
 */
export interface Promotion {
  /** Artikel-slug (= Firestore doc-id). */
  slug: string;
  /** Uitgelicht (toon op de homepage). */
  featured?: boolean;
  /** Homepage-plekken waar het product verschijnt (onafhankelijk, meerdere mogelijk):
   *  "row" (uitgelicht-rij), "deals" (blauwe actie-blok).
   *  @deprecated "hero" wordt niet meer geschreven (de hero gebruikt nu de Spotlight +
   *  heroRight uit settings/homepage); alleen nog backward-compat bij het lezen. */
  placements?: ("row" | "hero" | "deals")[];
  /** @deprecated oude enkelvoudige plek; alleen nog voor backward-read. */
  placement?: "row" | "hero" | "both" | "deals";
  /** Soort korting. */
  saleType?: "fixed" | "percent";
  /** Vaste actieprijs (excl. btw, centen) — bij saleType "fixed". */
  salePriceCents?: number;
  /** Kortingspercentage (1–99) — bij saleType "percent". */
  salePercent?: number;
  /** Geen prijs tonen: overal "Prijs op aanvraag" i.p.v. een prijs. */
  hidePrice?: boolean;
  /** Reviews verbergen op de uitgelicht-weergave (hero, rij, deals). */
  hideReviews?: boolean;
  /** Actie aan/uit. */
  active?: boolean;
  /** Eigen homepage-foto (data-URL), gebruikt i.p.v. de witte-achtergrond-productfoto
   *  in het uitgelicht-blok op de homepage. */
  heroImageData?: string;
  // Optionele homepage-overrides (leeg = automatisch afgeleid van het product).
  /** Labeltje (chip) i.p.v. "Actie"/"Uitgelicht". */
  chip?: string;
  /** Titel i.p.v. de productnaam. */
  title?: string;
  /** Ondertitel i.p.v. de groep / "Glas op maat". */
  subtitle?: string;
  /** Knoptekst i.p.v. "Bekijk product". */
  ctaLabel?: string;
  /** Knop-link i.p.v. de product-/configuratorpagina. */
  ctaTo?: string;
  /** Welke knop op de uitgelicht-kaart: groene "in winkelwagen" (product), oranje
   *  "configureer"-pijl (configurator), een neutrale pijl-link (artikel/blog), of "geen"
   *  (geen knop — de hele kaart blijft klikbaar). Leeg/undefined = automatisch (artikel met
   *  prijs → product, anders configurator). */
  ctaButton?: "product" | "configurator" | "artikel" | "geen";
}

/**
 * Effectieve actieprijs (excl. btw, centen) o.b.v. de normale prijs, of `null` als
 * er geen geldige, actieve korting is. Een actie telt alléén als de uitkomst écht
 * lager is dan de normale prijs (en > 0).
 */
export function salePriceCentsFor(baseCents: number, promo: Promotion | null | undefined): number | null {
  // "Geen prijs" sluit een korting uit (voorkomt ook een achtergebleven −% van een
  // eerder ingestelde korting).
  if (!promo || promo.active !== true || promo.hidePrice === true || !(baseCents > 0)) return null;
  let sale: number | null = null;
  if (promo.saleType === "fixed" && typeof promo.salePriceCents === "number" && Number.isFinite(promo.salePriceCents)) {
    sale = Math.round(promo.salePriceCents);
  } else if (promo.saleType === "percent" && typeof promo.salePercent === "number" && promo.salePercent > 0 && promo.salePercent < 100) {
    sale = Math.round(baseCents * (1 - promo.salePercent / 100));
  }
  if (sale === null || sale <= 0 || sale >= baseCents) return null;
  return sale;
}
